import React, { useState, useEffect, useCallback } from "react";
import {
  PageHeader, KPIGrid, DataTable, StandardModal, StatusBadge,
  Alert, Btn, FormField,
  inputStyle, selectStyle, textareaStyle,
  colors, spacing, typography,
} from "../design-system";

const BASE             = import.meta.env.VITE_API_URL || "http://localhost:8000/api";
const FEEDBACK_API     = `${BASE}/feedback`;
const COMPANIES_API    = `${BASE}/companies`;
const CERTS_API        = `${BASE}/certifications/certifications`;

const RECORD_TYPE_LABEL = {
  stage_1_audit: "Stage 1 Audit",
  stage_2_audit: "Stage 2 Audit",
  certification:  "Certification",
};

const AUDIT_TYPES = [
  "Initial Certification",
  "Surveillance Audit",
  "Recertification",
  "Special Audit",
  "Transfer Audit",
  "Follow-up Audit",
  "Stage 1",
  "Stage 2",
];

const RATING_LABELS = { 1: "Poor", 2: "Below Average", 3: "Satisfactory", 4: "Good", 5: "Excellent" };

const EMPTY_FORM = {
  company_id: "",
  certification_id: "",
  audit_date: "",
  audit_type: "",
  auditor_name: "",
  overall_rating: "",
  recommendation: "",
  notes: "",
  strengths: [""],
  improvements: [""],
  major_nonconformances: [""],
  minor_nonconformances: [""],
  observations: [""],
  opportunities_for_improvement: [""],
};

function ListField({ label, items, onChange }) {
  const add    = () => onChange([...items, ""]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const update = (i, val) => onChange(items.map((v, idx) => idx === i ? val : v));
  return (
    <FormField label={label}>
      <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
        {items.map((v, i) => (
          <div key={i} style={{ display: "flex", gap: spacing.xs }}>
            <input
              value={v}
              onChange={e => update(i, e.target.value)}
              style={{ ...inputStyle, flex: 1 }}
              placeholder={`${label} item ${i + 1}`}
            />
            {items.length > 1 && (
              <Btn size="sm" variant="ghost" onClick={() => remove(i)}>✕</Btn>
            )}
          </div>
        ))}
        <Btn size="sm" variant="ghost" onClick={add}>+ Add item</Btn>
      </div>
    </FormField>
  );
}

export default function AuditFeedback() {
  const [feedbacks,  setFeedbacks]  = useState([]);
  const [companies,  setCompanies]  = useState([]);
  const [audits,     setAudits]     = useState([]);   // audit records for selected company
  const [loading,    setLoading]    = useState(true);
  const [filterCo,   setFilterCo]   = useState("");
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null);  // feedback id being edited
  const [showDelete, setShowDelete] = useState(null);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [msg,        setMsg]        = useState("");
  const [msgType,    setMsgType]    = useState("success");

  const token   = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(""), 3500); };

  const fetchFeedbacks = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterCo ? `?company_id=${filterCo}` : "";
      const res  = await fetch(`${FEEDBACK_API}/${params}`, { headers });
      const data = res.ok ? await res.json() : [];
      setFeedbacks(Array.isArray(data) ? data : []);
    } catch { setFeedbacks([]); }
    finally { setLoading(false); }
  }, [filterCo]);

  useEffect(() => { fetchFeedbacks(); }, [fetchFeedbacks]);

  useEffect(() => {
    fetch(`${COMPANIES_API}/`, { headers })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCompanies(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  async function loadAuditsForCompany(companyId) {
    if (!companyId) { setAudits([]); return; }
    try {
      const res  = await fetch(`${CERTS_API}/?company_id=${companyId}`, { headers });
      const data = res.ok ? await res.json() : [];
      setAudits(Array.isArray(data) ? data : []);
    } catch { setAudits([]); }
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setAudits([]);
    setShowForm(true);
  }

  function openEdit(fb) {
    setEditing(fb.id);
    loadAuditsForCompany(fb.company_id);
    setForm({
      company_id:      fb.company_id,
      certification_id: fb.certification_id ?? "",
      audit_date:   fb.audit_date ? fb.audit_date.slice(0, 10) : "",
      audit_type:   fb.audit_type || "",
      auditor_name: fb.auditor_name || "",
      overall_rating: fb.overall_rating ?? "",
      recommendation: fb.recommendation || "",
      notes:        fb.notes || "",
      strengths:               fb.strengths?.length               ? fb.strengths               : [""],
      improvements:            fb.improvements?.length            ? fb.improvements            : [""],
      major_nonconformances:   fb.major_nonconformances?.length   ? fb.major_nonconformances   : [""],
      minor_nonconformances:   fb.minor_nonconformances?.length   ? fb.minor_nonconformances   : [""],
      observations:            fb.observations?.length            ? fb.observations            : [""],
      opportunities_for_improvement: fb.opportunities_for_improvement?.length ? fb.opportunities_for_improvement : [""],
    });
    setShowForm(true);
  }

  function setList(field, val) { setForm(f => ({ ...f, [field]: val })); }
  const clean = arr => arr.filter(v => v.trim() !== "");

  async function handleSave(e) {
    e.preventDefault();
    if (!form.company_id) { flash("Select a company.", "error"); return; }
    setSaving(true);
    try {
      const body = {
        company_id:      parseInt(form.company_id),
        certification_id: form.certification_id ? parseInt(form.certification_id) : null,
        audit_date:    form.audit_date ? new Date(form.audit_date).toISOString() : null,
        audit_type:    form.audit_type || null,
        auditor_name:  form.auditor_name || null,
        overall_rating: form.overall_rating !== "" ? parseInt(form.overall_rating) : null,
        recommendation: form.recommendation || null,
        notes:         form.notes || null,
        strengths:               clean(form.strengths),
        improvements:            clean(form.improvements),
        major_nonconformances:   clean(form.major_nonconformances),
        minor_nonconformances:   clean(form.minor_nonconformances),
        observations:            clean(form.observations),
        opportunities_for_improvement: clean(form.opportunities_for_improvement),
      };
      const url    = editing ? `${FEEDBACK_API}/${editing}` : `${FEEDBACK_API}/`;
      const method = editing ? "PATCH" : "POST";
      const res    = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (!res.ok) { const d = await res.json(); throw new Error(d.detail || "Failed"); }
      flash(editing ? "Feedback updated." : "Feedback created.");
      setShowForm(false);
      fetchFeedbacks();
    } catch (err) { flash("Error: " + err.message, "error"); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      const res = await fetch(`${FEEDBACK_API}/${showDelete}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Delete failed");
      flash("Feedback deleted.");
      setShowDelete(null);
      fetchFeedbacks();
    } catch (err) { flash("Error: " + err.message, "error"); }
    finally { setSaving(false); }
  }

  const thisMonth = feedbacks.filter(f => {
    if (!f.created_at) return false;
    const d = new Date(f.created_at);
    const n = new Date();
    return d.getMonth() === n.getMonth() && d.getFullYear() === n.getFullYear();
  }).length;

  const ratings = feedbacks.map(f => f.overall_rating).filter(Boolean);
  const avgRating = ratings.length ? (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1) : "—";

  const kpis = [
    { label: "Total Records",   value: feedbacks.length, icon: "📋", color: colors.primary },
    { label: "This Month",      value: thisMonth,        icon: "📅", color: colors.info },
    { label: "Avg Rating",      value: avgRating,        icon: "⭐", color: colors.success },
  ];

  const columns = [
    {
      key: "company_name", label: "Company",
      render: v => <span style={{ fontWeight: typography.fontWeights.semibold }}>{v || "—"}</span>,
    },
    {
      key: "certification_number", label: "Linked Audit",
      render: (v, row) => v ? (
        <span style={{ fontFamily: "monospace", fontSize: typography.fontSizes.xs, color: colors.primary }}>
          {row.iso_standard} — {RECORD_TYPE_LABEL[row.certification_type] || row.certification_type}<br/>
          <span style={{ color: colors.textMuted }}>{v}</span>
        </span>
      ) : <span style={{ color: colors.textMuted }}>—</span>,
    },
    {
      key: "audit_date", label: "Audit Date",
      render: v => v ? new Date(v).toLocaleDateString() : "—",
    },
    { key: "auditor_name", label: "Auditor", render: v => v || "—" },
    {
      key: "overall_rating", label: "Rating",
      render: v => v ? (
        <span style={{ color: v >= 4 ? colors.success : v <= 2 ? colors.danger : colors.warning }}>
          {"★".repeat(v)}{"☆".repeat(5 - v)} ({RATING_LABELS[v]})
        </span>
      ) : "—",
    },
    {
      key: "recommendation", label: "Recommendation",
      render: v => v ? <span style={{ color: colors.textSecondary }}>{v.slice(0, 50)}{v.length > 50 ? "…" : ""}</span> : "—",
    },
    {
      key: "_actions", label: "", align: "right",
      render: (_, row) => (
        <div style={{ display: "flex", gap: spacing.xs, justifyContent: "flex-end" }}>
          <Btn size="sm" variant="ghost" onClick={() => openEdit(row)}>Edit</Btn>
          <Btn size="sm" variant="danger" onClick={() => setShowDelete(row.id)}>Delete</Btn>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Audit Feedback"
        subtitle="Record and manage audit feedback that clients can view in their portal."
        actions={<Btn onClick={openCreate}>+ Add Feedback</Btn>}
      />

      <KPIGrid kpis={kpis} columns={3} />

      {msg && <Alert type={msgType} message={msg} onClose={() => setMsg("")} />}

      {/* Company filter */}
      <div style={{ marginBottom: spacing.lg }}>
        <select value={filterCo} onChange={e => setFilterCo(e.target.value)} style={{ ...selectStyle, width: "auto" }}>
          <option value="">All Companies</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={feedbacks}
        loading={loading}
        emptyMessage="No audit feedback records found."
        keyField="id"
      />

      {/* Create / Edit modal */}
      {showForm && (
        <StandardModal
          title={editing ? "Edit Audit Feedback" : "Add Audit Feedback"}
          onClose={() => setShowForm(false)}
          width="680px"
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowForm(false)}>Cancel</Btn>
              <Btn type="submit" form="feedback-form" loading={saving}>
                {editing ? "Save Changes" : "Create Feedback"}
              </Btn>
            </>
          }
        >
          <form id="feedback-form" onSubmit={handleSave}>
            <div style={{ display: "flex", flexDirection: "column", gap: spacing.md }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing.md }}>
                <FormField label="Conducted Audit" style={{ gridColumn: "1 / -1" }}>
                  <select
                    value={form.certification_id}
                    onChange={e => {
                      const id = e.target.value;
                      const audit = audits.find(a => String(a.id) === id);
                      setForm(f => ({
                        ...f,
                        certification_id: id,
                        audit_date:   audit?.audit_date   ? audit.audit_date.slice(0, 10)  : f.audit_date,
                        audit_type:   audit?.record_type  ? audit.record_type               : f.audit_type,
                        auditor_name: audit?.auditor_name ? audit.auditor_name              : f.auditor_name,
                      }));
                    }}
                    style={selectStyle}
                    disabled={!form.company_id}
                  >
                    <option value="">{form.company_id ? (audits.length ? "Select audit record…" : "No audits found for this company") : "Select a company first"}</option>
                    {audits.map(a => (
                      <option key={a.id} value={a.id}>
                        {RECORD_TYPE_LABEL[a.record_type] || a.record_type} — {a.iso_standard} — {a.audit_date ? new Date(a.audit_date).toLocaleDateString() : new Date(a.issue_date).toLocaleDateString()}{a.auditor_name ? ` (${a.auditor_name})` : ""}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Company" required>
                  <select
                    required
                    value={form.company_id}
                    onChange={e => {
                      const id = e.target.value;
                      setForm(f => ({ ...f, company_id: id, certification_id: "" }));
                      loadAuditsForCompany(id);
                    }}
                    style={selectStyle}
                    disabled={!!editing}
                  >
                    <option value="">Select company…</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </FormField>
                <FormField label="Audit Date">
                  <input
                    type="date"
                    value={form.audit_date}
                    onChange={e => setForm(f => ({ ...f, audit_date: e.target.value }))}
                    style={inputStyle}
                  />
                </FormField>
                <FormField label="Audit Type">
                  <select
                    value={form.audit_type}
                    onChange={e => setForm(f => ({ ...f, audit_type: e.target.value }))}
                    style={selectStyle}
                  >
                    <option value="">Select type…</option>
                    {AUDIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </FormField>
                <FormField label="Auditor Name">
                  <input
                    value={form.auditor_name}
                    onChange={e => setForm(f => ({ ...f, auditor_name: e.target.value }))}
                    style={inputStyle}
                    placeholder="Lead auditor name"
                  />
                </FormField>
                <FormField label="Overall Rating (1–5)">
                  <select
                    value={form.overall_rating}
                    onChange={e => setForm(f => ({ ...f, overall_rating: e.target.value }))}
                    style={selectStyle}
                  >
                    <option value="">Not rated</option>
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} — {RATING_LABELS[n]}</option>)}
                  </select>
                </FormField>
                <FormField label="Recommendation">
                  <input
                    value={form.recommendation}
                    onChange={e => setForm(f => ({ ...f, recommendation: e.target.value }))}
                    style={inputStyle}
                    placeholder="e.g. Recommend for certification"
                  />
                </FormField>
              </div>

              <ListField label="Strengths"                         items={form.strengths}                         onChange={v => setList("strengths", v)} />
              <ListField label="Improvements"                      items={form.improvements}                      onChange={v => setList("improvements", v)} />
              <ListField label="Major Nonconformances"             items={form.major_nonconformances}             onChange={v => setList("major_nonconformances", v)} />
              <ListField label="Minor Nonconformances"             items={form.minor_nonconformances}             onChange={v => setList("minor_nonconformances", v)} />
              <ListField label="Observations"                      items={form.observations}                      onChange={v => setList("observations", v)} />
              <ListField label="Opportunities for Improvement"     items={form.opportunities_for_improvement}     onChange={v => setList("opportunities_for_improvement", v)} />

              <FormField label="Additional Notes">
                <textarea
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  style={textareaStyle}
                />
              </FormField>
            </div>
          </form>
        </StandardModal>
      )}

      {/* Delete confirm */}
      {showDelete && (
        <StandardModal
          title="Delete Feedback"
          onClose={() => setShowDelete(null)}
          danger
          footer={
            <>
              <Btn variant="secondary" onClick={() => setShowDelete(null)}>Cancel</Btn>
              <Btn variant="danger" onClick={handleDelete} loading={saving}>Delete</Btn>
            </>
          }
        >
          <p style={{ color: colors.textSecondary, margin: 0 }}>
            This will permanently delete this feedback record. The client will no longer be able to see it.
          </p>
        </StandardModal>
      )}
    </div>
  );
}
